import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion } from 'framer-motion';
import { Star, ChevronLeft, ChevronRight } from 'lucide-react';

const Testimonials = () => {
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [visibleItems, setVisibleItems] = useState(1);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 1024) {
        setVisibleItems(3);
      } else if (window.innerWidth >= 768) {
        setVisibleItems(2);
      } else {
        setVisibleItems(1);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const fetchTestimonials = async () => {
      try {
        const response = await fetch('https://api.dev.auxo.dk/public/content/testimonials?t=' + Date.now());
        const data = await response.json();
        setTestimonials(data);
      } catch (error) {
        console.error('Error fetching testimonials:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestimonials();
  }, []);

  const maxIndex = Math.max(0, testimonials.length - visibleItems);

  useEffect(() => {
    if (currentIndex > maxIndex) {
      setCurrentIndex(maxIndex);
    }
  }, [maxIndex, currentIndex]);

  const nextSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev >= maxIndex ? 0 : prev + 1));
  }, [maxIndex]);

  const prevSlide = useCallback(() => {
    setCurrentIndex((prev) => (prev <= 0 ? maxIndex : prev - 1));
  }, [maxIndex]);

  if (loading || testimonials.length === 0) return null;

  return (
    <section className="py-12 md:py-24 bg-slate-100 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10">
          <h2 className="text-3xl md:text-4xl font-medium text-slate-900 mb-4 uppercase">
            Gæsterne rejser med os igen og igen
          </h2>
        </div>

        <div className="relative max-w-3xl mx-auto md:max-w-none">
          {/* Navigation Buttons */}
          <div className="absolute top-1/2 -translate-y-1/2 -left-2 md:-left-6 lg:-left-12 z-20">
            <button
              onClick={prevSlide}
              className="p-2.5 md:p-3 rounded-full bg-white border border-slate-200 text-[var(--brand)] hover:bg-slate-50 transition-all focus:outline-none shadow-md shadow-slate-200/50"
              aria-label="Forrige udtalelse"
            >
              <ChevronLeft size={20} className="md:w-6 md:h-6" />
            </button>
          </div>
          
          <div className="absolute top-1/2 -translate-y-1/2 -right-2 md:-right-6 lg:-right-12 z-20">
            <button
              onClick={nextSlide}
              className="p-2.5 md:p-3 rounded-full bg-white border border-slate-200 text-[var(--brand)] hover:bg-slate-50 transition-all focus:outline-none shadow-md shadow-slate-200/50"
              aria-label="Næste udtalelse"
            >
              <ChevronRight size={20} className="md:w-6 md:h-6" />
            </button>
          </div>

          {/* Carousel Container */}
          <div className="overflow-hidden px-2 py-2 md:py-4" ref={containerRef}>
            {visibleItems === 1 ? (
              // Mobile view: Render only the active testimonial to fit height perfectly
              <div className="min-h-[200px] flex items-center justify-center">
                {testimonials.map((testimonial, index) => {
                  if (index !== currentIndex) return null;
                  return (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: 20 }}
                      animate={{ opacity: 1, x: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      transition={{ duration: 0.3 }}
                      className="w-full px-3"
                    >
                      <div className="bg-white p-5 sm:p-6 rounded-3xl border border-slate-200 h-auto flex flex-col text-center items-center shadow-lg shadow-slate-200/60">
                        <div className="flex gap-1 text-[var(--brand)] mb-3">
                          {[...Array(testimonial.rating || 5)].map((_, i) => (
                            <Star key={i} size={18} fill="currentColor" />
                          ))}
                        </div>
                        <p className="text-base text-slate-700 italic leading-relaxed mb-4">
                          "
                          {testimonial.quote.da.replace(/\\n/g, '\n').split('\n').map((line, i) => (
                            <React.Fragment key={i}>
                              {line}
                              {i < testimonial.quote.da.replace(/\\n/g, '\n').split('\n').length - 1 && <br />}
                            </React.Fragment>
                          ))}
                          "
                        </p>
                        <div className="flex flex-col items-center gap-2 mt-auto">
                          <div className="w-10 h-1 bg-[var(--brand)] rounded-full"></div>
                          <span className="font-semibold text-base text-slate-900">
                            {testimonial.name}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              // Tablet/Desktop view: Standard flex slider
              <motion.div
                className="flex"
                animate={{
                  x: `-${currentIndex * (100 / testimonials.length)}%`
                }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 26
                }}
                style={{
                  width: `${(testimonials.length / visibleItems) * 100}%`
                }}
              >
                {testimonials.map((testimonial, index) => (
                  <div 
                    key={index} 
                    className="px-3"
                    style={{ width: `${100 / testimonials.length}%` }}
                  >
                    <div className="bg-white p-6 md:p-8 rounded-3xl border border-slate-200 h-full flex flex-col text-center items-center shadow-lg shadow-slate-200/60">
                      <div className="flex gap-1 text-[var(--brand)] mb-6">
                        {[...Array(testimonial.rating || 5)].map((_, i) => (
                          <Star key={i} size={18} fill="currentColor" />
                        ))}
                      </div>
                      <p className="text-base sm:text-lg text-slate-700 italic leading-relaxed mb-8">
                        "
                        {testimonial.quote.da.replace(/\\n/g, '\n').split('\n').map((line, i) => (
                          <React.Fragment key={i}>
                            {line}
                            {i < testimonial.quote.da.replace(/\\n/g, '\n').split('\n').length - 1 && <br />}
                          </React.Fragment>
                        ))}
                        "
                      </p>
                      <div className="flex flex-col items-center gap-3 mt-auto">
                        <div className="w-10 h-1 bg-[var(--brand)] rounded-full"></div>
                        <span className="font-semibold text-base text-slate-900">
                          {testimonial.name}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </motion.div>
            )}
          </div>

        </div>
      </div>
    </section>
  );
};

export default Testimonials;
